/**
 * Console Find — everything the results page shows *about* its results.
 *
 * The searching itself belongs to the AdvancedSearch class in script.js, which
 * predates the Console and stays in charge of it. This module listens for the
 * `uf:searchresults` event that class fires after every render and paints four
 * things from it: the facet counts beside each filter option, the count line
 * under the heading, the note saying what the trust floor is holding back, and
 * the parsed reading of the query.
 *
 * It also binds "Save as alert", which is the only thing here that writes.
 *
 * A listener rather than a call site inside AdvancedSearch: that class should
 * not have to know which theme is on, and the same event will serve whatever
 * later surface wants to describe the same results.
 */

import { FLOOR_NAMES, getFloor, onFloorChange } from './floor-state.js';
import { getFloorCounts } from './console-stats.js';
import { DEFAULT_INTENT, parseInput } from './trusted-query-parser.js';

const NUM = (value) => Number(value || 0).toLocaleString();

/* ------------------------------------------------------------ the query -- */

/**
 * The query as typed, with the operator tokens picked out.
 *
 * Shown as the page's h1 in mono, so what you get back is visibly the thing
 * you typed rather than a sentence about it. Anything of the form `word:value`
 * or a `$100-200` range is marked; everything else is the keyword.
 */
function paintQuery(raw) {
  const title = document.querySelector('[data-find-title]');
  const parsed = document.querySelector('[data-find-parsed]');
  const query = (raw || '').trim();

  if (title) {
    if (!query) {
      title.textContent = 'Find';
    } else {
      title.textContent = '';
      for (const token of query.split(/\s+/)) {
        const span = document.createElement('span');
        span.textContent = token;
        if (/^[a-z]+:\S+$/i.test(token) || /^\$?\d[\d,.]*-\$?\d[\d,.]*$/.test(token)) {
          span.className = 'op';
        }
        title.appendChild(span);
        title.appendChild(document.createTextNode(' '));
      }
    }
  }

  if (!parsed) return null;
  if (!query) {
    parsed.hidden = true;
    return null;
  }

  let result = null;
  try {
    result = parseInput(query);
  } catch {
    result = null;
  }
  const intent = result && result.intent;
  if (!intent) {
    parsed.hidden = true;
    return null;
  }

  // Only what the query actually *said*. The parser fills an intent with
  // defaults -- returnsAccepted is true in every intent, typed or not -- so
  // reading the intent straight would have the line claim the visitor asked
  // for things they never typed. Each part is shown only where the intent
  // differs from the empty one.
  const differs = (key) => intent[key] !== DEFAULT_INTENT[key];
  const parts = [];
  if (intent.query) parts.push(`query “${intent.query}”`);
  if (differs('minPrice') || differs('maxPrice')) {
    parts.push(`price ${intent.minPrice ?? 0}–${intent.maxPrice ?? '∞'}`);
  }
  if (differs('condition')) parts.push(`condition ${String(intent.condition).toLowerCase()}`);
  if (differs('returnsAccepted')) {
    parts.push(intent.returnsAccepted ? 'returns required' : 'returns not required');
  }
  if (differs('freeShipping')) parts.push('free shipping');
  if (differs('listingType')) parts.push(`listing ${String(intent.listingType).toLowerCase()}`);
  if (differs('minQuantity')) parts.push(`quantity ${intent.minQuantity}+`);
  if (differs('includeForParts')) parts.push('including for-parts listings');

  // An operator the parser did not understand is the one thing worth saying
  // even when nothing else was parsed: it looks like it worked otherwise.
  const ignored = (result.warnings || [])
    .filter((warning) => warning && warning.message)
    .map((warning) => warning.message);

  // "query macbook" on its own restates the heading; the line earns its space
  // only once an operator has been read out of the query — or misread.
  parsed.hidden = parts.length < 2 && !ignored.length;
  parsed.textContent = [
    parts.length ? `parsed: ${parts.join(' · ')}` : '',
    ...ignored,
  ].filter(Boolean).join(' · ');
  return intent;
}

/* ------------------------------------------------------------ the pane --- */

/** Write `n` into the option's count badge, creating it the first time. */
function paintCount(input, value) {
  const label = input.closest('label');
  if (!label) return;
  let badge = label.querySelector('.n');
  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'n';
    label.appendChild(badge);
  }
  badge.textContent = NUM(value);
  // An option that would return nothing is dimmed rather than removed: it is
  // still a fact about this result set, and options that come and go under the
  // pointer are worse than options that are visibly empty.
  label.classList.toggle('empty', !value);
}

function paintFacets(facets) {
  if (!facets) return;
  for (const [selector, counts] of [
    ['#conditionFilterOptions input[type="checkbox"]', facets.conditions],
    ['#categoryFilterOptions input[type="checkbox"]', facets.categories],
  ]) {
    if (!counts) continue;
    for (const input of document.querySelectorAll(selector)) {
      if (input.value in counts) paintCount(input, counts[input.value]);
    }
  }
}

/**
 * What the floor is holding back, under the floor group.
 *
 * The one number the pane can honestly show about the floor. Per-floor counts
 * ("Standard 71 · Strict 38") would need the unfiltered set, and these results
 * were fetched at a floor — see the partial's comment.
 */
function paintFloorNote(floor) {
  const note = document.querySelector('[data-floor-hidden]');
  if (!note) return;
  // Scoped to the query when there is one. Straight off `lastTrustFloor` this
  // line described the whole catalog, so a search that matched two listings
  // sat under "23 hidden at Strict" -- a number about listings the reader had
  // not asked for, beside a grid that had counted differently.
  const counts = getFloorCounts();
  const hidden = counts ? (counts.below || 0) + (counts.unrated || 0) : 0;
  if (!counts || !hidden) {
    note.textContent = '';
    note.hidden = true;
    return;
  }
  const parts = [];
  if (counts.below) parts.push(`${NUM(counts.below)} below it`);
  if (counts.unrated) parts.push(`${NUM(counts.unrated)} with no seller rating`);
  note.hidden = false;
  note.textContent =
    `${NUM(hidden)} hidden at ${FLOOR_NAMES[floor] || floor} — ${parts.join(', ')}.`;
}

/* ---------------------------------------------------------- the header --- */

function paintCounts(detail) {
  const line = document.querySelector('[data-find-count]');
  const foot = document.querySelector('[data-find-footnote]');
  const shown = (detail.shown || []).length;
  // `detail.all` is everything the page loaded, which is the purchasable
  // catalog -- the keyword is applied in the browser. Saying "0 of 12
  // listings" over a query that matched none of those 12 states a total that
  // has nothing to do with what was asked. When the query's own counts are in
  // hand, its total is the honest denominator.
  const counts = getFloorCounts();
  const all = counts && Number.isFinite(Number(counts.total))
    ? Number(counts.total)
    : (detail.all || []).length;

  if (line) {
    line.textContent = shown === all
      ? `${NUM(shown)} listing${shown === 1 ? '' : 's'}`
      : `${NUM(shown)} of ${NUM(all)} listings`;
  }
  if (foot) {
    foot.textContent = shown
      ? `Showing ${NUM(shown)} of ${NUM(all)} listings.`
      : '';
  }
}

/* ------------------------------------------------------ save as alert ---- */

function alertPayloadFrom(filters) {
  const query = (filters && filters.query ? String(filters.query) : '').trim();
  if (!query) return null;
  return {
    query,
    ebayCategoryId: (filters.categories && filters.categories[0]) || '',
    maxPrice: filters.maxPrice != null ? filters.maxPrice : null,
  };
}

function say(button, message) {
  let note = document.getElementById('findAlertStatus');
  if (!note) {
    note = document.createElement('span');
    note.id = 'findAlertStatus';
    note.className = 'vh';
    note.setAttribute('aria-live', 'polite');
    button.after(note);
  }
  note.textContent = message;
  button.dataset.said = message;
  button.title = message;
}

function bindSaveAlert(getFilters) {
  const button = document.querySelector('[data-save-alert]');
  if (!button || button.dataset.bound === '1') return;
  button.dataset.bound = '1';

  button.addEventListener('click', async () => {
    const guest = button.dataset.saveAlertGuest;
    if (guest) {
      window.location.href = guest;
      return;
    }
    const payload = alertPayloadFrom(getFilters());
    if (!payload) {
      say(button, 'Search for something first — an alert needs a query to match.');
      return;
    }
    button.disabled = true;
    try {
      await window.apiService.createSavedSearch(payload);
      button.textContent = 'Alert saved';
      // Name what was saved. SavedSearch matches on query, category, condition
      // and a price ceiling; it does not carry the trust floor, and a button
      // that implied otherwise would promise alerts this site cannot send.
      say(button, `Alert saved for “${payload.query}”. It does not carry your trust floor.`);
    } catch (error) {
      button.disabled = false;
      say(button, 'Could not save that alert. Try again in a moment.');
    }
  });
}

/* ------------------------------------------------------------- wiring ---- */

let lastFilters = {};

function init() {
  if (!document.body || !document.body.classList.contains('console')) return;
  if (!document.querySelector('[data-find-title]')) return;   // not the Find page

  paintQuery(new URLSearchParams(window.location.search).get('q'));
  paintFloorNote(getFloor());
  bindSaveAlert(() => lastFilters);

  let lastDetail = {};
  document.addEventListener('uf:searchresults', (event) => {
    const detail = event.detail || {};
    lastDetail = detail;
    lastFilters = detail.filters || {};
    paintCounts(detail);
    paintFacets(detail.facets);
    paintFloorNote(getFloor());
  });

  // The query's own floor counts cost a request, so they land after the render
  // that `uf:searchresults` announced. Both lines above read them, so both are
  // repainted when they arrive -- with the last detail, not a fresh filter
  // pass, so this cannot feed back into the search that triggered it.
  document.addEventListener('uf:floorcounts', () => {
    paintCounts(lastDetail);
    paintFloorNote(getFloor());
  });

  onFloorChange(paintFloorNote);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export { paintQuery, paintFacets, alertPayloadFrom };
